import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import BrandMark from 'components/BrandMark';
import useAuth from 'hooks/useAuth';
import axios from 'api/axios';

const LEGACY_TO_CONTEXT_ROUTE_MAP = {
  '/': '/ghana/dashboard',
  '/dashboard': '/ghana/dashboard',
  '/national-dashboard': '/ghana/dashboard',
  '/regional-dashboard': '/ghana/dashboard',
  '/district-dashboard': '/ghana/dashboard',
  '/meetings-management': '/ghana/trackers',
  '/program-performance': '/ghana/reports',
  '/dpat-dashboard': '/ghana/dpat-dashboard',
  '/reports': '/ghana/reports',
  '/dpat-assessment': '/ghana/dpat-assessment',
  '/organizational-hierarchy': '/ghana/rcc',
  '/automated-report-generation-system': '/setup/ohlgs-setup',
  '/settings': '/ghana/settings',
};

const getPostLoginRedirect = (fromLocation) => {
  const pathname = fromLocation?.pathname || '/';
  const search = fromLocation?.search || '';
  const hash = fromLocation?.hash || '';

  if (pathname.startsWith('/ghana')) {
    return `${pathname}${search}${hash}`;
  }

  return LEGACY_TO_CONTEXT_ROUTE_MAP[pathname] || '/ghana/dashboard';
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useAuth();
  
  // State Management
  const [username, setUsername] = useState("");
  const [districtCount, setDistrictCount] = useState(0);
  const [regionCount, setRegionCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Added for visibility toggle
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const postLoginRedirect = getPostLoginRedirect(location?.state?.from);

  useEffect(() => {
    getData("/organisationUnits?level=3&paging=false&fields=id,displayName,parent[id,displayName]");  
    getCounter("/organisationUnits?level=3&paging=false&fields=id,displayName,parent[id,displayName]");
  }, [])

  function getData(url) {
    const storedDistricts = localStorage.getItem("districts");
    if (storedDistricts) {
      return;
    }

    axios
      .get(url)
      .then((result) => {
        let temp = [];
        const locations = result.data.organisationUnits;
        const actualLocations = locations.filter(location => location.parent && location.parent.displayName !== "AO Region");
        locations.forEach((district) => {
          const currentDistrict = { value: { id: district.id, region: district.parent.displayName }, label: district.displayName };
          temp.push(currentDistrict);
        });

        const uniqueRegions = new Set(actualLocations.map(loc => loc.parent.displayName));
        setRegionCount(uniqueRegions.size);

        setDistrictCount(actualLocations.length);

        localStorage.setItem("districts", JSON.stringify(temp));
      })
      .catch((err) => console.log(err));
  }

  function getCounter(url) {
    axios
      .get(url)
      .then((result) => {
        const locations = result.data.organisationUnits;
        const actualLocations = locations.filter(location => location.parent && location.parent.displayName !== "AO Region");
        
        const uniqueRegions = new Set(actualLocations.map(loc => loc.parent.displayName));
        const totalRegions = uniqueRegions.size;
        const totalDistricts = actualLocations.length;
        
        setRegionCount(totalRegions);
        setDistrictCount(totalDistricts);
        
        return axios.get("/users?pageSize=1&fields=id");
      })
      .then((userResult) => {
        const totalUsers = userResult.data.pager.total;
        setUserCount(totalUsers);
      })
      .catch((err) => console.log(err));
}

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const authResponse = await axios.get(
        `/me?fields=username,displayName,email,phoneNumber,disabled,userCredentials[userRoles[id,name]],organisationUnits[id,name,level,path,parent[id,name]],userGroups[id,name],dataViewOrganisationUnits[id,name,level]`,
        {
          auth: {
            username: username,
            password: password
          }
        }
      );

      if (!authResponse.data || !authResponse.data.username) {
        throw new Error("Authentication failed");
      }

      const userData = authResponse.data;

      if (userData.disabled) {
        setError("Your account is disabled. Please contact administrator.");
        setIsLoading(false);
        return;
      }

      const credentials = btoa(`${username}:${password}`);
      sessionStorage.setItem('dhis2_auth', credentials);
      sessionStorage.setItem('dhis2_username', username);

      const payload = {
        user: {
          fullName: userData.displayName,
          username: userData.username,
          phone: userData.phoneNumber,
          disabled: userData.disabled,
          email: userData.email,
          userRoles: userData.userCredentials?.userRoles || [],
          organisationUnits: userData.organisationUnits || [],
          dataViewOrganisationUnits: userData.dataViewOrganisationUnits || [],
          userGroups: userData.userGroups || [],
          regions: userData.organisationUnits?.filter(org => org.level === 2) || [],
          districts: userData.organisationUnits?.filter(org => org.level === 3) || [],
          communities: userData.organisationUnits?.filter(org => org.level === 4) || [],
          authCredentials: credentials
        }
      };

      dispatch({ type: 'LOGIN_SUCCESS', payload: payload });
      navigate(postLoginRedirect, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      if (err.response) {
        if (err.response.status === 401) {
          setError("Invalid username or password. Please try again.");
        } else if (err.response.status === 403) {
          setError("You don't have permission to access this application.");
        } else {
          setError(`Authentication failed: ${err.response.status} error`);
        }
      } else if (err.request) {
        setError("Cannot connect to dddp server. Please check your connection.");
      } else {
        setError("An error occurred. Please try again.");
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#ffffff' }}>
      {/* Left Panel: Desktop Only */}
      <div className="hidden lg:flex lg:flex-1 items-center justify-center p-12" style={{ background: 'linear-gradient(135deg, #16325a 0%, #0d1f3a 100%)', borderTopRightRadius: '2rem', borderBottomRightRadius: '2rem' }}>
        <div className="max-w-md text-white" style={{ animation: 'fadeIn 0.6s ease-in' }}>
          <div className="flex items-center gap-3 mb-8">
            <BrandMark
              alt="DDDP logo"
              className="h-14 w-14 rounded-xl bg-white p-2 shadow-md"
            />
            <div>
              <h1 className="text-2xl font-bold" style={{ letterSpacing: '-0.02em' }}>DDDP</h1>
              <p className="text-sm opacity-80" style={{ fontWeight: '500' }}>District Development Data Platform</p>
            </div>
          </div>
          <h2 className="text-4xl font-bold mb-4 leading-tight" style={{ letterSpacing: '-0.03em', fontWeight: '700' }}>
            District Development Data Platform
          </h2>
          <p className="text-lg opacity-80 leading-relaxed" style={{ fontWeight: '400' }}>
            Web based planning, monitoring & evaluation and reporting platform.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 text-center">
            {[
              { label: "Regions", value: regionCount.toLocaleString() },
              { label: "Districts", value: districtCount.toLocaleString() },
              { label: "Users", value: userCount.toLocaleString() },
            ].map((s) => (
              <div key={s.label} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)' }}>
                <div className="text-2xl font-bold" style={{ fontWeight: '700', letterSpacing: '-0.02em' }}>{s.value}</div>
                <div className="text-xs opacity-70" style={{ fontWeight: '500' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel: Login Form */}
      <div className="flex-1 flex items-center justify-center p-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="w-full max-w-sm" style={{ animation: 'fadeIn 0.6s ease-in' }}>
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <BrandMark
              alt="DDDP logo"
              className="h-8 w-8 rounded-lg border border-slate-200 bg-white p-1"
            />
            <span className="font-bold text-lg">DDDP</span>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-2" style={{ letterSpacing: '-0.02em', fontWeight: '700' }}>Welcome back</h3>
            <p className="text-gray-600" style={{ fontWeight: '400' }}>Sign in with your DDDP credentials</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium text-gray-700" style={{ fontWeight: '600' }}>Username</label>
              <input
                id="username"
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16325a] focus:border-transparent transition-all"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your dddp username"
                required
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-700" style={{ fontWeight: '600' }}>Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16325a] focus:border-transparent transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your dddp password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-[#16325a] transition-colors rounded-md focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                <Icon name="AlertCircle" size={16} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3 px-4 text-white rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{
                background: 'linear-gradient(135deg, #16325a 0%, #0d1f3a 100%)',
                fontWeight: '600',
                letterSpacing: '0.01em'
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;